import { IconButton, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { ReactElement, ReactNode, useState } from 'react';
import { tokens } from '@synergy/theme-palo';

export interface PaloOverflowAction {
    label: ReactNode;
    onClick: () => void;
    disabled?: boolean;
    /**
     * Why the entry cannot be used, shown on hover while it is disabled.
     *
     * A greyed-out entry says nothing about what would make it work, which is
     * the one thing wanted at that moment (#2214, R-42).
     */
    reason?: ReactNode;
    /** Deleting and the like: shown in the warning colour, set apart by a rule. */
    danger?: boolean;
    /** A rule below the entry, setting it apart from the ones that follow. */
    separated?: boolean;
    /** Keyboard shortcut, printed next to the entry the way ERPNext does. */
    shortcut?: string;
    /** Shown in the row itself while it is the row's only action (#2341). */
    icon?: ReactNode;
}

export interface PaloOverflowMenuProps {
    actions: PaloOverflowAction[];
    /** Named for a screen reader; the trigger itself is only three dots. */
    label?: string;
}

/**
 * Everything a mask can do beyond its one next step.
 *
 * A document mask used to carry every action as a button of its own, which
 * made the row of them wider than the screen on a laptop and put deleting next
 * to saving. One button stays outside — the next logical step — and the rest
 * lives here, in the same place on every mask (#2206).
 */
export const PaloOverflowMenu = ({
    actions,
    label = 'Weitere Aktionen',
}: PaloOverflowMenuProps): ReactElement | null => {
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);

    if (0 === actions.length) {
        return null;
    }

    const ordered = [...actions.filter(action => !action.danger), ...actions.filter(action => action.danger)];
    const firstDanger = ordered.findIndex(action => action.danger);

    return (
        <>
            <Tooltip title={label}>
                <IconButton size="small" aria-label={label} onClick={event => setAnchor(event.currentTarget)}>
                    <MoreHoriz fontSize="small" />
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {ordered.map((action, index) => (
                    // A disabled entry answers no mouse, so the reason hangs on
                    // a wrapper around it rather than on the entry itself.
                    <Tooltip key={index} title={action.disabled && action.reason ? action.reason : ''} placement="left">
                        <span>
                            <MenuItem
                                disabled={action.disabled}
                                sx={{
                                    gap: '24px',
                                    width: '100%',
                                    borderTop:
                                        index === firstDanger && index > 0
                                            ? `1px solid ${tokens.color.line}`
                                            : undefined,
                                    borderBottom:
                                        action.separated && index < ordered.length - 1
                                            ? `1px solid ${tokens.color.line}`
                                            : undefined,
                                }}
                                onClick={() => {
                                    setAnchor(null);
                                    action.onClick();
                                }}
                            >
                                <ListItemText
                                    primaryTypographyProps={{
                                        fontSize: '13px',
                                        color: action.danger ? tokens.color.crit : tokens.color.ink,
                                    }}
                                >
                                    {action.label}
                                </ListItemText>
                                {action.shortcut && (
                                    <ListItemText
                                        sx={{ flex: '0 0 auto' }}
                                        primaryTypographyProps={{
                                            fontSize: '11px',
                                            fontFamily: tokens.font.mono,
                                            color: tokens.color.ink3,
                                        }}
                                    >
                                        {action.shortcut}
                                    </ListItemText>
                                )}
                            </MenuItem>
                        </span>
                    </Tooltip>
                ))}
            </Menu>
        </>
    );
};
